package com.sddamico.swigmore.ds.forms

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.SwType
import com.sddamico.swigmore.ds.css
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Input as DomInput
import org.w3c.dom.HTMLInputElement

/**
 * - [Search]: borderless cream field on the green panel ("Search teams…").
 * - [Form]: bordered light-cream field on a cream card (sign-in email).
 * - [Code]: the six-digit OTP entry — big spaced Graduate numerals, centred.
 */
enum class TextFieldVariant { Search, Form, Code }

private class InputHandle { var element: HTMLInputElement? = null }

@Composable
fun TextField(
  value: String,
  onValueChange: (String) -> Unit,
  modifier: Modifier = Modifier,
  variant: TextFieldVariant = TextFieldVariant.Form,
  placeholder: String? = null,
  type: String = "text",
  label: String? = placeholder,
  onEnter: (() -> Unit)? = null,
) {
  val currentOnValueChange = rememberUpdatedState(onValueChange)
  val currentOnEnter = rememberUpdatedState(onEnter)
  val handle = remember { InputHandle() }
  // Written only when it differs, so typing doesn't move the cursor.
  SideEffect { handle.element?.let { if (it.value != value) it.value = value } }
  val look = when (variant) {
    TextFieldVariant.Search -> Modifier.css(
      "padding" to "10px 12px", "border-radius" to "7px", "border" to "none",
      "background" to SwColor.cream, "font" to "500 14px ${SwType.body}")
    TextFieldVariant.Form -> Modifier.css(
      "padding" to "14px", "border-radius" to SwEffects.radiusMd, "border" to "1.5px solid ${SwColor.borderOnCream}",
      "background" to SwColor.creamLight, "font" to "500 16px ${SwType.body}")
    TextFieldVariant.Code -> Modifier.css(
      "padding" to "12px 14px", "border-radius" to SwEffects.radiusMd, "border" to "1.5px solid ${SwColor.borderOnCream}",
      "background" to SwColor.creamLight, "font" to "400 34px ${SwType.display}", "letter-spacing" to ".4em",
      "text-align" to "center")
  }
  DomInput(
    type = InputType.Text,
    attrs = Modifier
      .css("width" to "100%", "box-sizing" to "border-box", "color" to SwColor.field700, "outline" to "none")
      .then(look)
      .then(modifier)
      .toAttrs {
        attr("type", if (variant == TextFieldVariant.Code) "text" else type)
        if (variant == TextFieldVariant.Code) {
          attr("inputmode", "numeric")
          attr("autocomplete", "one-time-code")
        }
        if (placeholder != null) attr("placeholder", placeholder)
        if (label != null) attr("aria-label", label)
        onInput { ev ->
          val raw = ev.value
          val next = if (variant == TextFieldVariant.Code) raw.filter(Char::isDigit).take(6) else raw
          // A rejected keystroke leaves state unchanged, so no recomposition would correct the DOM.
          if (next != raw) handle.element?.value = next
          currentOnValueChange.value(next)
        }
        onKeyDown { ev -> if (ev.key == "Enter") { ev.preventDefault(); currentOnEnter.value?.invoke() } }
        ref { input ->
          handle.element = input
          input.value = value
          onDispose { handle.element = null }
        }
      }
  )
}
